import { EXCEL_PT_BR, INTERNACIONAL } from '@equiview/domain';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { IconeDocumento, IconeRelogio, IconeSelo, IconeTabela } from '../components/Icones.js';
import { SemCadastro } from '../components/SemCadastro.js';
import { ChipsDeVisoes } from '../components/VisoesRapidas.js';
import {
  Aguardar,
  Alternador,
  BarraFiltros,
  BarrasHorizontais,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  CampoFiltro,
  CartaoGrafico,
  Etiqueta,
  FaixaContextoFiltro,
  FaixaIndicadores,
  Indicador,
  Lista,
  Painel,
  Rosca,
  Seletor,
  type ColunaLista,
} from '../components/ui/index.js';
import { formatarData, hojeIso } from '../dados/catalogo.js';
import {
  contarSituacoes,
  descreverConsulta,
  filtrarFichas,
  filtrosAtivosConsulta,
  lerFiltrosConsulta,
  ordenarFichas,
  PERIODOS,
  ROTULO_NIVEL,
  ROTULO_PERIODO,
  ROTULO_SITUACAO_FICHA,
  SITUACOES,
  situacaoDaFicha,
  type OrdemFichas,
  type SituacaoFicha,
} from '../dados/consulta.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { baixarArquivo, csvLargo, csvLongo } from '../dados/exportar.js';
import type { Avaliacao, Visita } from '../dados/fonte.js';
import { minutosDaFicha } from './Coleta.js';

const ORDENS: OrdemFichas[] = ['data', 'animal', 'medidas'];

/**
 * Consulta e exportacao: o dado sai do aparelho numa planilha, sem pedir nada
 * ao dev. Formato longo para o R e o ICC; uma linha por ficha para o Excel.
 * Filtros na URL: as visoes rapidas ("Fichas por validar") sao so um link.
 */
export function Dados() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(async (f) => {
    const [visitas, animais, avaliacoes] = await Promise.all([f.listarVisitas(), f.listarAnimais(), f.avaliacoes()]);
    const daVisita = new Set(visitas.map((v) => v.id));
    const fichas = avaliacoes.filter((a) => daVisita.has(a.visit_id));
    const ids = fichas.map((a) => a.id);
    const [medidas, observacoes, manejos] = await Promise.all([f.medidas(ids), f.observacoes(ids), f.manejos(ids)]);
    return { visitas, animais, fichas, medidas, observacoes, manejos };
  }, []);

  if (!equipe && !fonte.ficticia) return <SemCadastro demo={false} />;

  return <Aguardar consulta={consulta}>{(d) => <Consulta {...d} />}</Aguardar>;
}

function Consulta({
  visitas,
  animais,
  fichas,
  medidas,
  observacoes,
  manejos,
}: {
  visitas: Visita[];
  animais: Parameters<typeof csvLongo>[0]['animais'];
  fichas: Avaliacao[];
  medidas: Parameters<typeof csvLargo>[1];
  observacoes: Parameters<typeof csvLongo>[1];
  manejos: Parameters<typeof csvLargo>[3];
}) {
  const [params, setParams] = useSearchParams();
  const hoje = hojeIso();
  const f = lerFiltrosConsulta(params);
  const ordem: OrdemFichas = ORDENS.find((o) => o === params.get('ordem')) ?? 'data';
  const direcao = params.get('dir') === 'asc' ? 'asc' : 'desc';
  const [formato, setFormato] = useState<'excel' | 'internacional'>('excel');

  const nome = useMemo(() => new Map(animais.map((a) => [a.id, a.name])), [animais]);
  const numero = useMemo(() => new Map(visitas.map((v) => [v.id, v.visit_number])), [visitas]);
  const contagem = useMemo(() => {
    const c = new Map<string, number>();
    for (const m of medidas) if (m.value_num != null || m.not_applicable) c.set(m.assessment_id, (c.get(m.assessment_id) ?? 0) + 1);
    return c;
  }, [medidas]);

  const chave = params.toString();
  // Um recorte so: indicadores, graficos, lista e exportacao leem `filtradas`.
  const filtradas = useMemo(
    () => ordenarFichas(filtrarFichas(fichas, f, hoje), ordem, direcao, nome, contagem),
    // f, ordem e direcao saem de `params`: a chave da URL basta como dependencia.
    [fichas, chave, hoje, nome, contagem],
  );
  const ids = useMemo(() => new Set(filtradas.map((a) => a.id)), [filtradas]);
  const ativos = filtrosAtivosConsulta(f);
  const situacoes = contarSituacoes(filtrarFichas(fichas, f, hoje, ['situacao']));
  const doRecorte = contarSituacoes(filtradas);
  const semVisita = filtrarFichas(fichas, f, hoje, ['visita']);
  const tempos = filtradas
    .filter((a) => a.ended_at && a.protocol_level >= 2)
    .map((a) => minutosDaFicha(a))
    .filter((m): m is number => m != null);
  const tempoMedio = tempos.length ? Math.round(tempos.reduce((x, y) => x + y, 0) / tempos.length) : null;

  const mudar = (novos: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novos)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true, preventScrollReset: true });
  };
  const limpar = () => mudar({ visita: null, nivel: null, situacao: null, periodo: null, de: null, ate: null });

  const formatoCsv = formato === 'excel' ? EXCEL_PT_BR : INTERNACIONAL;
  const ctx = { animais, visitas, avaliacoes: filtradas };
  const sufixo = `${hoje}${formato === 'excel' ? '' : '-intl'}`;
  const contexto: ReactNode[] = ativos
    ? [
        <span key="n">
          <b>
            {filtradas.length} de {fichas.length}
          </b>{' '}
          fichas
        </span>,
        ...descreverConsulta(f, visitas, hoje),
      ]
    : [];

  const colunas: ColunaLista<Avaliacao>[] = [
    {
      id: 'data',
      rotulo: 'Data',
      largura: '110px',
      celula: (a) => formatarData(a.visit_date),
      dirPadrao: 'desc',
    },
    {
      id: 'medidas',
      rotulo: 'Medidas',
      largura: '90px',
      celula: (a) => contagem.get(a.id) ?? 0,
      medida: (a) => contagem.get(a.id) ?? 0,
    },
    {
      id: 'tempo',
      rotulo: 'Duração',
      largura: '90px',
      ordenavel: false,
      celula: (a) => {
        const m = a.ended_at ? minutosDaFicha(a) : null;
        return m == null ? '—' : `${m} min`;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        titulo="Consulta e exportação"
        meta={<span>As fichas deste haras numa planilha, para o Excel ou para o R.</span>}
        acoes={<BotaoLink to="/repetibilidade">Relatório de repetibilidade</BotaoLink>}
      />

      <ChipsDeVisoes />

      <BarraFiltros
        topo={
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[10.5px] font-semibold tracking-[0.08em] text-apagado uppercase">Período</span>
            <Alternador
              rotulo="Período"
              valor={f.periodo || 'tudo'}
              onMudar={(v) => mudar({ periodo: v === 'tudo' ? null : v, de: null, ate: null })}
              opcoes={[
                { valor: 'tudo', rotulo: 'Tudo', titulo: 'Todas as datas' },
                ...PERIODOS.map((p) => ({
                  valor: p,
                  rotulo: ROTULO_PERIODO[p],
                  titulo: p === 'pers' ? 'Escolher as datas' : `Os últimos ${ROTULO_PERIODO[p]}, até hoje`,
                })),
              ]}
            />
          </div>
        }
        ativos={ativos}
        onLimpar={limpar}
        contagem={
          <>
            <b>{filtradas.length}</b> {filtradas.length === 1 ? 'ficha' : 'fichas'}
            {ativos > 0 && ` de ${fichas.length}`}
          </>
        }
        campos={
          <>
            <CampoFiltro
              rotulo="Visita"
              todos="Todas as visitas"
              valor={f.visita}
              onMudar={(v) => mudar({ visita: v || null })}
              opcoes={visitas.map((v) => ({
                valor: v.id,
                rotulo: `Nº ${v.visit_number} · ${formatarData(v.visit_date)}${v.purpose === 'estudo_repetibilidade' ? ' · estudo' : ''}`,
              }))}
            />
            <CampoFiltro
              rotulo="Nível"
              todos="Todos"
              valor={f.nivel}
              onMudar={(v) => mudar({ nivel: v || null })}
              opcoes={(['1', '2'] as const).map((n) => ({ valor: n, rotulo: ROTULO_NIVEL[n] }))}
            />
            <CampoFiltro
              rotulo="Situação"
              todos="Todas"
              valor={f.situacao}
              onMudar={(v) => mudar({ situacao: v || null })}
              opcoes={SITUACOES.map((s) => ({ valor: s, rotulo: ROTULO_SITUACAO_FICHA[s] }))}
            />
            {f.periodo === 'pers' && (
              <>
                <CampoData rotulo="De" valor={f.de} onMudar={(v) => mudar({ de: v || null })} />
                <CampoData rotulo="Até" valor={f.ate} onMudar={(v) => mudar({ ate: v || null })} />
              </>
            )}
          </>
        }
      />

      <FaixaIndicadores rotulo="Resumo das fichas">
        <Indicador
          destaque
          rotulo={ativos ? 'Fichas no recorte' : 'Fichas'}
          valor={filtradas.length}
          icone={<IconeTabela width={17} height={17} />}
          complemento={ativos ? `de ${fichas.length} do haras` : `em ${visitas.length} visita(s)`}
        />
        <Indicador
          rotulo="Em aberto"
          valor={doRecorte.abertas}
          icone={<IconeDocumento width={17} height={17} />}
          complemento="rascunho ou reaberta"
        />
        <Indicador
          rotulo="Por validar"
          valor={doRecorte.fechadas}
          icone={<IconeSelo width={17} height={17} />}
          complemento="fechadas, sem validação"
        />
        <Indicador
          rotulo="Validadas"
          valor={doRecorte.validadas}
          icone={<IconeSelo width={17} height={17} />}
          complemento={filtradas.length ? `${Math.round((doRecorte.validadas / filtradas.length) * 100)}% do recorte` : '—'}
        />
        <Indicador
          rotulo="Tempo médio"
          valor={tempoMedio == null ? '—' : `${tempoMedio} min`}
          icone={<IconeRelogio width={17} height={17} />}
          complemento={`ficha completa · ${tempos.length} medida(s)`}
        />
      </FaixaIndicadores>

      <FaixaContextoFiltro partes={contexto} />

      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <CartaoGrafico titulo="Fichas por visita" sub="Clique numa visita para filtrar. As mais cheias primeiro.">
          <BarrasHorizontais
            rotulo="Fichas por visita"
            formatar={String}
            itens={visitas.map((v) => {
              const n = semVisita.filter((a) => a.visit_id === v.id).length;
              return {
                chave: v.id,
                rotulo: `Nº ${v.visit_number} · ${formatarData(v.visit_date)}`,
                valor: n,
                sub: v.purpose === 'estudo_repetibilidade' ? 'estudo' : undefined,
              };
            })}
            ativo={f.visita || null}
            onEscolher={(id) => mudar({ visita: f.visita === id ? null : id })}
            vazio="Nenhuma ficha no recorte."
          />
        </CartaoGrafico>
        <CartaoGrafico titulo="Fichas por situação" sub="Do rascunho à validação. Clique para filtrar.">
          <Rosca
            rotulo="Fichas por situação"
            unidade="fichas"
            foco={f.situacao || null}
            onEscolher={(s) => mudar({ situacao: f.situacao === s ? null : s })}
            fatias={(
              [
                ['validadas', 1],
                ['fechadas', 2],
                ['abertas', 3],
              ] as const
            ).map(([s, passo]) => ({ chave: s, rotulo: ROTULO_SITUACAO_FICHA[s], valor: situacoes[s], passo }))}
            vazio="Nenhuma ficha no recorte."
          />
        </CartaoGrafico>
      </div>

      <Painel
        titulo={`Exportar ${filtradas.length} ficha(s)`}
        acoes={
          <Seletor
            rotulo="Formato"
            valor={formato}
            onMudar={setFormato}
            opcoes={[
              { valor: 'excel', conteudo: <span className="px-1 py-2">Excel em português</span> },
              { valor: 'internacional', conteudo: <span className="px-1 py-2">Internacional (R/Python)</span> },
            ]}
          />
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Botao
            variante="principal"
            disabled={filtradas.length === 0}
            onClick={() =>
              baixarArquivo(
                `equiview-fichas-${sufixo}.csv`,
                csvLargo(ctx, medidas.filter((m) => ids.has(m.assessment_id)), formatoCsv, manejos),
              )
            }
          >
            Uma linha por ficha
          </Botao>
          <Botao
            disabled={filtradas.length === 0}
            onClick={() =>
              baixarArquivo(
                `equiview-observacoes-${sufixo}.csv`,
                csvLongo(ctx, observacoes.filter((o) => ids.has(o.assessment_id)), formatoCsv),
              )
            }
          >
            Formato longo (todas as sessões)
          </Botao>
        </div>
        <p className="mt-3 text-xs text-apagado">
          "Uma linha por ficha" traz a primeira medição, com uma coluna por medida e lado, os índices e a Estação 1. O
          formato longo traz uma linha por observação, de todas as sessões (dupla medição e estudo) — é o do R e do ICC.
        </p>
      </Painel>

      <Lista<Avaliacao>
        key={chave}
        titulo="Fichas"
        dica="Abra uma linha para o detalhe · linha apagada: ficha de censo (nível 1)"
        linhas={filtradas}
        chave={(a) => a.id}
        rotuloItem={(a) => `${nome.get(a.animal_id) ?? 'ficha'} em ${formatarData(a.visit_date)}`}
        rotuloPrincipal="Ficha"
        ordemPrincipal={{ id: 'animal', rotulo: 'Animal' }}
        colunas={colunas}
        ordem={{ col: ordem, dir: direcao }}
        onOrdenar={(o) => mudar({ ordem: o.col === 'data' ? null : o.col, dir: o.dir === 'asc' ? 'asc' : null })}
        fraca={(a) => a.protocol_level < 2}
        passo={50}
        vazio={
          <span className="space-y-3">
            <span className="block">Nenhuma ficha com esses filtros.</span>
            {ativos > 0 && (
              <Botao tamanho="sm" onClick={limpar}>
                Limpar filtros
              </Botao>
            )}
          </span>
        }
        principal={(a) => (
          <div className="min-w-0">
            <Link
              to={`/animais/${a.animal_id}?ficha=${a.id}`}
              className="alvo-toque line-clamp-2 font-serif text-[15px] leading-snug font-semibold text-marca hover:underline"
            >
              {nome.get(a.animal_id) ?? '—'}
            </Link>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-apagado">
              <EtiquetaSituacao s={situacaoDaFicha(a)} reaberta={a.status === 'reaberta'} />
              <span aria-hidden className="opacity-50">
                ·
              </span>
              Visita nº {numero.get(a.visit_id) ?? '—'}
              <span aria-hidden className="opacity-50">
                ·
              </span>
              {a.protocol_level >= 2 ? 'Completa' : 'Censo'}
            </p>
          </div>
        )}
        detalhe={(a) => (
          <div className="space-y-3.5">
            <dl className="grid gap-x-8 gap-y-1 text-[13px] sm:grid-cols-2">
              <LinhaDetalhe rotulo="Situação">{a.status}</LinhaDetalhe>
              <LinhaDetalhe rotulo="Nível">{a.protocol_level}</LinhaDetalhe>
              <LinhaDetalhe rotulo="Início da coleta">
                {a.started_at ? new Date(a.started_at).toLocaleString('pt-BR') : '—'}
              </LinhaDetalhe>
              <LinhaDetalhe rotulo="Fechamento">
                {a.ended_at ? new Date(a.ended_at).toLocaleString('pt-BR') : '—'}
              </LinhaDetalhe>
              <LinhaDetalhe rotulo="Dupla medição">{a.sampled_for_double_measure ? 'Sorteada' : 'Não'}</LinhaDetalhe>
              <LinhaDetalhe rotulo="Medidas preenchidas">{contagem.get(a.id) ?? 0}</LinhaDetalhe>
            </dl>
            <div className="flex flex-wrap gap-2 border-t border-borda pt-3.5">
              <BotaoLink tamanho="sm" variante="principal" to={`/animais/${a.animal_id}?ficha=${a.id}`}>
                Abrir a ficha
              </BotaoLink>
              <BotaoLink tamanho="sm" to={`/visitas/${a.visit_id}`}>
                Abrir a visita
              </BotaoLink>
            </div>
          </div>
        )}
      />
    </div>
  );
}

function EtiquetaSituacao({ s, reaberta }: { s: SituacaoFicha; reaberta: boolean }) {
  if (s === 'validadas') return <Etiqueta tom="ok">Validada</Etiqueta>;
  if (s === 'fechadas') return <Etiqueta tom="destaque">Fechada</Etiqueta>;
  return <Etiqueta tom="aviso">{reaberta ? 'Reaberta' : 'Em aberto'}</Etiqueta>;
}

function LinhaDetalhe({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-apagado">{rotulo}</dt>
      <dd className="numero min-w-0 truncate text-right font-semibold">{children}</dd>
    </div>
  );
}

/** Data na barra de filtros, no mesmo desenho do CampoFiltro. */
function CampoData({ rotulo, valor, onMudar }: { rotulo: string; valor: string; onMudar: (v: string) => void }) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-[10.5px] font-semibold tracking-[0.08em] text-apagado uppercase">{rotulo}</span>
      <input
        type="date"
        value={valor}
        onChange={(e) => onMudar(e.target.value)}
        className={`w-full rounded-(--radius-controle) border bg-superficie px-3 py-2 text-[13px] font-medium hover:border-borda-forte ${
          valor ? 'border-destaque' : 'border-borda'
        }`}
      />
    </label>
  );
}
